import React, { useState, useEffect, useRef } from 'react';
import { FiUsers, FiClock, FiDownload, FiX, FiSearch, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';
import api from '../utils/api';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const VerticalHeadsOverview = () => {
    const [heads, setHeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedHead, setSelectedHead] = useState(null);
    const [teamDetails, setTeamDetails] = useState([]);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [year, setYear] = useState(new Date().getFullYear());
    const [searchTerm, setSearchTerm] = useState('');
    const scrollRef = useRef(null);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [leaveDetails, setLeaveDetails] = useState([]);
    const [leaveDetailsLoading, setLeaveDetailsLoading] = useState(false);
    const [leaveType, setLeaveType] = useState('');

    const scroll = (direction) => {
        const { current } = scrollRef;
        if (current) {
            const scrollAmount = 400;
            current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
        }
    };

    const fetchHeads = async () => {
        try {
            setLoading(true);
            const res = await api.get('/hrms/vertical-heads-overview', { params: { year } });
            if (res.data.status) {
                // Sort heads by total_leaves descending
                const sortedHeads = [...res.data.data].sort((a, b) => b.total_leaves - a.total_leaves);
                setHeads(sortedHeads);
            }
        } catch (error) {
            console.error('Error fetching vertical heads:', error);
            toast.error('Failed to load Vertical Heads overview');
        } finally {
            setLoading(false);
        }
    };

    const fetchTeamDetails = async (headId) => {
        try {
            setDetailsLoading(true);
            const res = await api.get('/hrms/vertical-heads-team-details', {
                params: { vertical_head_id: headId, year }
            });
            if (res.data.status) {
                setTeamDetails(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching team details:', error);
            toast.error('Failed to load team details');
        } finally {
            setDetailsLoading(false);
        }
    };

    const fetchLeaveDetails = async (headId, type) => {
        try {
            setLeaveDetailsLoading(true);
            setLeaveType(type);
            const res = await api.get('/hrms/vertical-heads-leave-details', {
                params: { vertical_head_id: headId, year, type }
            });
            if (res.data.status) {
                setLeaveDetails(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching leave details:', error);
            toast.error('Failed to load leave details');
        } finally {
            setLeaveDetailsLoading(false);
        }
    };

    useEffect(() => {
        fetchHeads();
    }, [year]);

    const handleHeadClick = (head) => {
        setSelectedHead(head);
        fetchTeamDetails(head.id);
        setShowModal(true);
    };

    const exportToExcel = async () => {
        if (!teamDetails.length) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Team Leave Analysis');

        // 1. Report Title
        const titleRow = worksheet.addRow(['TEAM LEAVE ANALYSIS REPORT']);
        worksheet.mergeCells(`A${titleRow.number}:E${titleRow.number}`);
        titleRow.font = { name: 'Arial Black', size: 16, color: { argb: 'FFFFFFFF' } };
        titleRow.alignment = { vertical: 'middle', horizontal: 'center' };
        titleRow.getCell(1).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1F2937' } // Dark Gray
        };

        // 2. Metadata
        const metaData = [
            [`Vertical Head: ${selectedHead?.name}`],
            [`Year: ${year}`],
            [`Generated On: ${new Date().toLocaleString()}`],
            [] // Spacer
        ];

        metaData.forEach(data => {
            const row = worksheet.addRow(data);
            worksheet.mergeCells(`A${row.number}:E${row.number}`);
            if (data.length > 0) {
                row.font = { bold: true };
            }
        });

        // 3. Table Headers
        const headerRow = worksheet.addRow(["EMPLOYEE NAME", "TOTAL LEAVE", "UNSCHEDULED LEAVES", "PERMISSION COUNT", "PERMISSION HOURS"]);
        headerRow.height = 25;

        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF1F2937' } // Match title background
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // 4. Data Rows
        teamDetails.forEach(emp => {
            const row = worksheet.addRow([
                emp.name,
                emp.total_leave_count,
                emp.unscheduled_leave_count,
                emp.permission_count,
                emp.permission_hours
            ]);

            // Apply styling to each cell in the row
            for (let i = 1; i <= 5; i++) {
                const cell = row.getCell(i);
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };

                cell.alignment = {
                    vertical: 'middle',
                    horizontal: i === 1 ? 'left' : 'center'
                };
                cell.font = { size: 10 };
            }
        });

        worksheet.columns = [
            { width: 35 }, // Employee
            { width: 18 }, // Total Leaves
            { width: 22 }, // Unscheduled
            { width: 20 }, // Permission Count
            { width: 20 }  // Permission Hours
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Team_Leave_Analysis_${selectedHead.name.replace(/\s+/g, '_')}_${year}.xlsx`);
    };

    const filteredHeads = heads.filter(head =>
        head.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        head.designation.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const SparklineChart = ({ data, color }) => {
        const options = {
            chart: {
                backgroundColor: null,
                borderWidth: 0,
                type: 'area',
                margin: [2, 0, 2, 0],
                width: 100,
                height: 30,
                style: { overflow: 'visible' },
                skipClone: true
            },
            title: { text: '' },
            credits: { enabled: false },
            exporting: { enabled: false },
            xAxis: {
                labels: { enabled: false },
                title: { text: null },
                startOnTick: false,
                endOnTick: false,
                tickPositions: [],
                lineWidth: 0,
                minorGridLineWidth: 0,
                lineColor: 'transparent'
            },
            yAxis: {
                endOnTick: false,
                startOnTick: false,
                labels: { enabled: false },
                title: { text: null },
                tickPositions: [0],
                gridLineWidth: 0,
                lineColor: 'transparent'
            },
            legend: { enabled: false },
            tooltip: { enabled: false },
            plotOptions: {
                series: {
                    animation: false,
                    lineWidth: 2,
                    shadow: false,
                    states: { hover: { lineWidth: 2 } },
                    marker: { radius: 1, states: { hover: { radius: 2 } } },
                    fillOpacity: 0.1
                },
                column: { negativeColor: '#910000', borderColor: 'silver' }
            },
            series: [{
                data: data,
                color: color
            }]
        };

        return <HighchartsReact highcharts={Highcharts} options={options} />;
    };

    if (loading && !heads.length) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
            </div>
        );
    }

    return (
        <div className="w-full bg-[#f8fafc] py-8 px-4 sm:px-6">
            <div className="max-w-[1600px] mx-auto">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Team Leave Overview</h2>
                        <p className="text-sm text-gray-500 mt-1">Leave trends and metrics for department heads</p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
                        <div className="relative w-full sm:w-64">
                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search Head..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-100 outline-none transition-all w-full shadow-sm"
                            />
                        </div>
                        <div className="relative group/select w-full sm:w-auto">
                            <select
                                value={year}
                                onChange={(e) => setYear(parseInt(e.target.value))}
                                className="appearance-none bg-white border border-gray-200 rounded-xl pl-4 pr-10 py-2.5 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-red-100 transition-all shadow-sm cursor-pointer hover:border-red-200 w-full sm:min-w-[80px]"
                                style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                            >
                                {[0, 1, 2].map(i => {
                                    const y = new Date().getFullYear() - i;
                                    return <option key={y} value={y}>{y}</option>
                                })}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover/select:text-red-500 transition-colors">
                                {/* <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"> */}
                                {/* <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" /> */}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative group/scroll-container">
                    {/* Floating Scroll Buttons */}
                    <button
                        onClick={() => scroll('left')}
                        className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white border border-gray-100 rounded-full shadow-xl text-gray-600 hover:bg-gray-50 hover:text-red-600 transition-all duration-300 opacity-0 group-hover/scroll-container:opacity-100 scale-90 group-hover/scroll-container:scale-100 active:scale-95"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                    </button>

                    <button
                        onClick={() => scroll('right')}
                        className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white border border-gray-100 rounded-full shadow-xl text-gray-600 hover:bg-gray-50 hover:text-red-600 transition-all duration-300 opacity-0 group-hover/scroll-container:opacity-100 scale-90 group-hover/scroll-container:scale-100 active:scale-95"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                    </button>

                    <div
                        ref={scrollRef}
                        className="flex overflow-x-auto gap-6 pb-6 no-scrollbar scroll-smooth px-2"
                    >
                        {filteredHeads.map(head => {
                            const leaveTrend = head.trend?.map(t => t.leaves) || [];
                            const unscheduledTrend = head.trend?.map(t => t.unscheduled) || [];
                            const permissionTrend = head.trend?.map(t => t.permission_hours) || [];

                            return (
                                <div
                                    key={head.id}
                                    onClick={() => handleHeadClick(head)}
                                    className="min-w-[320px] max-w-[320px] bg-white rounded-3xl shadow-sm border border-gray-100 p-6 cursor-pointer hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 group"
                                >
                                    <div className="flex items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-50">
                                        <div className="flex items-center gap-4 min-w-0">
                                            <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100 flex-shrink-0 border-4 border-white shadow-md group-hover:border-red-50 transition-all duration-500">
                                                {head.profile_image ? (
                                                    <img src={head.profile_image} alt={head.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100 text-red-500 font-bold text-xl uppercase">
                                                        {head.name.charAt(0)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className="font-bold text-gray-800 text-lg truncate group-hover:text-red-600 transition-colors uppercase tracking-tight">{head.name}</h3>
                                                <p className="text-[10px] text-gray-400 font-medium truncate uppercase tracking-tight">({head.designation})</p>
                                            </div>
                                        </div>
                                        <div className="shrink-0">
                                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-100/50 text-gray-500 rounded-xl text-[11px] font-black shadow-sm group-hover:bg-red-50 group-hover:border-red-100 group-hover:text-red-600 transition-all duration-300">
                                                <FiUsers size={12} className="text-gray-400 group-hover:text-red-500" />
                                                {head.team_count || 0}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        {/* Total Leaves Row */}
                                        <div
                                            onClick={(e) => { e.stopPropagation(); setSelectedHead(head); fetchLeaveDetails(head.id, 'total'); setShowLeaveModal(true); }}
                                            className="flex items-center justify-between group/row p-3 rounded-2xl bg-blue-50/30 border border-transparent hover:border-blue-100 transition-all"
                                        >
                                            <div className="flex flex-col">
                                                <p className="text-[11px] font-bold text-blue-600/70 uppercase tracking-widest mb-0.5">Total Leaves</p>
                                                <div className="flex items-center gap-1.5">
                                                    <SparklineChart data={leaveTrend} color="#3b82f6" />
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-2xl font-black text-blue-900 leading-none">{head.total_leaves}</p>
                                                <p className="text-[10px] font-bold text-blue-400 mt-1 flex items-center justify-end gap-1 uppercase">
                                                    <FiTrendingUp size={10} /> +{head.today_leaves || 0}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Unscheduled Row */}
                                        <div
                                            onClick={(e) => { e.stopPropagation(); setSelectedHead(head); fetchLeaveDetails(head.id, 'unscheduled'); setShowLeaveModal(true); }}
                                            className="flex items-center justify-between group/row p-3 rounded-2xl bg-orange-50/30 border border-transparent hover:border-orange-100 transition-all"
                                        >
                                            <div className="flex flex-col">
                                                <p className="text-[11px] font-bold text-orange-600/70 uppercase tracking-widest mb-0.5">Unscheduled</p>
                                                <div className="flex items-center gap-1.5">
                                                    <SparklineChart data={unscheduledTrend} color="#f97316" />
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-2xl font-black text-orange-900 leading-none">{head.unscheduled_leaves}</p>
                                                <p className="text-[10px] font-bold text-orange-400 mt-1 flex items-center justify-end gap-1 uppercase">
                                                    <FiTrendingUp size={10} /> +{head.today_unscheduled || 0}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Permissions Row */}
                                        <div
                                            onClick={(e) => { e.stopPropagation(); setSelectedHead(head); fetchLeaveDetails(head.id, 'permission'); setShowLeaveModal(true); }}
                                            className="flex items-center justify-between group/row p-3 rounded-2xl bg-emerald-50/30 border border-transparent hover:border-emerald-100 transition-all"
                                        >
                                            <div className="flex flex-col">
                                                <p className="text-[11px] font-bold text-emerald-600/70 uppercase tracking-widest mb-0.5">Permissions</p>
                                                <div className="flex items-center gap-1.5">
                                                    <SparklineChart data={permissionTrend} color="#10b981" />
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="flex flex-col items-end">
                                                    <p className="text-2xl font-black text-emerald-900 leading-none">{head.total_permissions}</p>
                                                    <p className="text-[10px] text-emerald-600/60 font-bold uppercase mt-1">({head.total_permission_hours}h)</p>
                                                </div>
                                                <p className="text-[10px] font-bold text-emerald-400 mt-1 flex items-center justify-end gap-1 uppercase">
                                                    <FiTrendingUp size={10} /> +{head.today_permissions || 0}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <style jsx="true">{`
                    .no-scrollbar::-webkit-scrollbar { display: none; }
                    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                `}</style>

                    {/* Team Details Modal */}
                    {showModal && (
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999] p-4">
                            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-300">
                                <div className="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
                                    <div className="flex items-center gap-4 sm:gap-5">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500 shadow-inner">
                                            <FiUsers size={20} className="sm:w-6 sm:h-6" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg sm:text-xl font-black text-gray-800 tracking-tight">Team Leave Analysis</h3>
                                            <p className="text-[10px] sm:text-xs text-gray-400 font-medium tracking-wide">Reporting to: <span className="text-red-600 font-bold uppercase tracking-tight">{selectedHead?.name}</span></p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={exportToExcel}
                                            className="flex items-center gap-2 px-4 py-2 bg-[#047857] hover:bg-[#064e3b] text-white rounded-lg transition-all text-[11px] font-bold shadow-md active:scale-95"
                                        >
                                            <FiDownload className="text-sm" />
                                            <span>Export</span>
                                        </button>
                                        <button
                                            onClick={() => setShowModal(false)}
                                            className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-600 transition-all border border-transparent hover:border-gray-200"
                                        >
                                            <FiX size={20} />
                                        </button>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50/30">
                                    {detailsLoading ? (
                                        <div className="flex flex-col items-center justify-center py-16 gap-5">
                                            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-500/20 border-b-red-500"></div>
                                            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Compiling Team Data</p>
                                        </div>
                                    ) : teamDetails.length > 0 ? (
                                        <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-xl">
                                            <table className="w-full text-left border-collapse min-w-[600px]">
                                                <thead>
                                                    <tr className="bg-gray-50/80">
                                                        <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Team Member</th>
                                                        <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100 text-center">Leaves</th>
                                                        <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100 text-center">Unscheduled</th>
                                                        <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100 text-center">Permissions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {teamDetails.map((emp, idx) => (
                                                        <tr key={emp.id} className="hover:bg-red-50/40 transition-all duration-300 group">
                                                            <td className="py-3 px-6">
                                                                <div className="flex items-center gap-4">
                                                                    <div className="w-8 h-8 rounded-lg bg-gray-100/80 flex items-center justify-center text-gray-400 font-black text-[10px] group-hover:bg-red-100 group-hover:text-red-500 transition-all duration-500 border border-gray-200/50">
                                                                        {idx + 1}
                                                                    </div>
                                                                    <span className="font-bold text-gray-800 group-hover:text-red-700 transition-colors text-xs sm:text-[14px]">{emp.name}</span>
                                                                </div>
                                                            </td>
                                                            <td className="py-3 px-6 text-center">
                                                                <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-lg font-black text-[11px] border border-blue-100/50">
                                                                    {emp.total_leave_count}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 px-6 text-center">
                                                                <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 rounded-lg font-black text-[11px] border border-orange-100/50">
                                                                    {emp.unscheduled_leave_count}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 px-6 text-center">
                                                                <div className="inline-flex flex-col items-center bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-100/50">
                                                                    <span className="text-emerald-700 font-black text-[11px]">{emp.permission_count}</span>
                                                                    <span className="text-[8px] font-bold text-emerald-600/60 uppercase tracking-tighter">({emp.permission_hours}h)</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-24 text-gray-300 bg-white rounded-3xl border border-dashed border-gray-200">
                                            <FiUsers size={64} className="mb-6 opacity-20" />
                                            <p className="font-black text-xs uppercase tracking-widest text-gray-400">No Team History Found</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Leave Details Modal */}
                    {showLeaveModal && (
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
                            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-300">
                                <div className="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
                                    <div className="flex items-center gap-4 sm:gap-5">
                                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-inner ${leaveType === 'total' ? 'bg-blue-50 text-blue-500' :
                                            leaveType === 'unscheduled' ? 'bg-orange-50 text-orange-500' : 'bg-emerald-50 text-emerald-500'
                                            }`}>
                                            <FiClock size={20} className="sm:w-6 sm:h-6" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg sm:text-xl font-black text-gray-800 tracking-tight">
                                                {leaveType === 'total' ? 'Total Leaves' :
                                                    leaveType === 'unscheduled' ? 'Unscheduled Leaves' : 'Permissions'} Detail
                                            </h3>
                                            <p className="text-[10px] sm:text-xs text-gray-400 font-medium tracking-wide">Reporting to: <span className="text-red-600 font-bold uppercase tracking-tight">{selectedHead?.name}</span></p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setShowLeaveModal(false)}
                                            className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-600 transition-all border border-transparent hover:border-gray-200"
                                        >
                                            <FiX size={20} />
                                        </button>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50/30">
                                    {leaveDetailsLoading ? (
                                        <div className="flex flex-col items-center justify-center py-16 gap-5">
                                            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-500/20 border-b-red-500"></div>
                                            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Compiling Records</p>
                                        </div>
                                    ) : leaveDetails.length > 0 ? (
                                        <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-xl">
                                            <table className="w-full text-left border-collapse min-w-[700px]">
                                                <thead>
                                                    <tr className="bg-gray-50/80">
                                                        <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Employee</th>
                                                        <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100 text-center">Date</th>
                                                        <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100 text-center">Duration</th>
                                                        <th className="py-4 font-black text-[10px] uppercase tracking-[0.2em] text-gray-500 px-6 border-b border-gray-100">Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {leaveDetails.map((leave, idx) => (
                                                        <tr key={idx} className="hover:bg-red-50/40 transition-all duration-300 group">
                                                            <td className="py-3 px-6">
                                                                <span className="font-bold text-gray-800 group-hover:text-red-700 transition-colors text-xs sm:text-[14px]">{leave.employee_name}</span>
                                                            </td>
                                                            <td className="py-3 px-6 text-center">
                                                                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded-lg font-bold text-[11px]">
                                                                    {leave.date_text}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 px-6 text-center">
                                                                <span className={`inline-block px-3 py-1 rounded-lg font-black text-[11px] ${leaveType === 'total' ? 'bg-blue-50 text-blue-700' :
                                                                    leaveType === 'unscheduled' ? 'bg-orange-50 text-orange-700' : 'bg-emerald-50 text-emerald-700'
                                                                    }`}>
                                                                    {leave.duration_text}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 px-6">
                                                                <p className="text-[11px] text-gray-500 italic line-clamp-2 max-w-[250px]">{leave.reason || 'No reason provided'}</p>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-24 text-gray-300 bg-white rounded-3xl border border-dashed border-gray-200">
                                            <FiClock size={64} className="mb-6 opacity-20" />
                                            <p className="font-black text-xs uppercase tracking-widest text-gray-400">No Records Found</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerticalHeadsOverview;
